import type { TranslationCatalog } from './types';

export const en: TranslationCatalog = {
  localeName: 'English',
  dir: 'ltr',
  brand: { wordmark: 'please.repair', product: 'Please.repair' },
  nav: {
    signIn: 'Sign in',
    tenants: 'For tenants',
    advocates: 'For advocates',
    landlords: 'For owners and agents',
    joinWaitlist: 'Join the waitlist',
    dashboard: 'Dashboard', // drafted — not in design corpus
    knowledge: 'Knowledge', // drafted — not in design corpus
  },
  hero: {
    title: 'Rental repairs, sorted properly.',
    subtitle:
      'Please.repair tracks every repair request from first report to job done. If it stalls, it walks you through the next legal step in your state or territory.',
    cta: 'Join the waitlist',
  },
  audiences: {
    tenant: {
      title: "I'm a tenant",
      blurb: 'Report a problem, keep your evidence safe, and know what to do if nothing happens.',
    },
    advocate: {
      title: "I'm helping a tenant",
      blurb:
        'A friend, family member, caseworker or legal centre. Run the case beside them, calmly and by the book.',
    },
    landlord: {
      title: "I'm an owner or property manager",
      blurb:
        'One queue for every request, with the paper trail that keeps you compliant and out of hearings.',
    },
  },
  waitlist: {
    heading: 'Get Please.repair first',
    emailLabel: 'Your email',
    submit: 'Join the waitlist',
    success: "You're on the list. Talk soon.",
    finePrint: 'No spam, roughly one email a month.',
  },
  signIn: {
    heading: 'Sign in or create your account',
    stages: ['Email', 'Verify', 'Legal', 'Roles', 'Places'],
    comingNote: 'The full app is coming soon. What you see here is a preview.', // drafted — not in design corpus
  },
  roles: {
    tenant: 'Tenant',
    advocate: 'Advocate',
    propertyManager: 'Property manager',
    owner: 'Owner',
    tradie: 'Tradie',
  },
  statuses: {
    open: 'Open',
    acknowledged: 'Acknowledged',
    scheduled: 'Scheduled',
    awaitingQuote: 'Awaiting quote',
    quoted: 'Quoted',
    overdue: 'Overdue',
    escalated: 'Escalated',
    resolved: 'Resolved',
  },
  severities: { urgent: 'Urgent', high: 'High', routine: 'Routine' },
  dashboard: {
    title: 'Everything dashboard',
    kpiOpen: 'Open cases',
    kpiDueWeek: 'Due this week',
    kpiOverdue: 'Overdue',
    kpiResolved30: 'Resolved · 30 days',
    filterAll: 'All',
    filterOpen: 'Open',
    filterDueSoon: 'Due soon',
    filterOverdue: 'Overdue',
    searchPlaceholder: 'Search cases, properties, people',
    emptyQueue: 'Nothing here yet',
    timelineHeading: 'Timeline',
    deadlineHeading: 'Statutory deadline',
    sampleDataBadge: 'Sample data', // drafted — not in design corpus
    chartTitle: 'Open cases by severity', // drafted — not in design corpus
    viewAsTable: 'View as table', // drafted — not in design corpus
    viewAsChart: 'View as chart', // drafted — not in design corpus
  },
  knowledge: {
    heading: 'Knowledge', // drafted — not in design corpus
    intro:
      'The rules for rental repairs in every state and territory: urgent repair lists, repair timeframes, tribunals and where to get help.', // ADR 0018 — the list holds statutory clocks AND practice benchmarks
    disclaimer:
      'Please.repair organises facts and points to official rules and forms. It does not give legal advice.',
    lastVerified: 'Last verified', // drafted — not in design corpus
    urgentHeading: 'Urgent repairs', // drafted — not in design corpus
    clocksHeading: 'Repair timeframes', // ADR 0018 — the list holds statutory clocks AND benchmarks
    bodiesHeading: 'Tribunals and bodies', // drafted — not in design corpus
    helpHeading: 'Where to get help', // drafted — not in design corpus
    citationsHeading: 'References',
    verifyFlagsHeading: 'Verification flags', // drafted — not in design corpus
    clockStatutory: 'Statutory · {period}', // ADR 0018 — the corpus's own unit, formatted by Intl
    clockBenchmark: 'Practice benchmark · about {period}', // ADR 0018 — not law
  },
  a11y: {
    skipToContent: 'Skip to content',
    opensInNewTab: 'opens in a new tab',
    loading: 'Loading…', // drafted — not in design corpus
  },
  errors: {
    generic: 'Something went wrong.', // drafted — not in design corpus
    retry: 'Try again', // drafted — not in design corpus
    notFound: 'Page not found', // drafted — not in design corpus
    notFoundBody: 'That page does not exist or has moved.', // drafted — not in design corpus
    serverError: 'Something went wrong', // drafted — not in design corpus; 500 page (#376)
    serverErrorBody: 'This page could not be shown. Try again, or start from the home page.', // drafted — #376
    offline: 'You are offline', // drafted — not in design corpus; offline page (#376)
    offlineBody: 'Please.repair needs a connection to load this page. Reconnect and try again.', // drafted — #376
  },
  meta: {
    home: {
      title: 'Please.repair — rental repairs on the record',
      description:
        'Please.repair — rental repairs on the record. Report issues, keep evidence, track statutory repair deadlines, and coordinate everyone involved.',
    },
    tenants: {
      title: 'For tenants — Please.repair',
      description:
        'Report a problem, keep your evidence safe, and know what to do if nothing happens.',
    },
    advocates: {
      title: 'For advocates — Please.repair',
      description:
        'A friend, family member, caseworker or legal centre. Run the case beside them, calmly and by the book.',
    },
    landlords: {
      title: 'For owners and agents — Please.repair',
      description:
        'One queue for every request, with the paper trail that keeps you compliant and out of hearings.',
    },
    signIn: {
      title: 'Sign in — Please.repair',
      description: 'Sign in or create your Please.repair account.',
    },
    dashboard: {
      title: 'Dashboard — Please.repair',
      description: 'Open cases, deadlines and recent activity in one queue.',
    },
    knowledge: {
      title: 'Rental repair rules by state and territory — Please.repair',
      description:
        'The rules for rental repairs in every state and territory: urgent repair lists, repair timeframes, tribunals and where to get help.',
    },
    knowledgeDetail: {
      title: 'Rental repair rules — Please.repair',
      description:
        'Urgent repair lists, repair timeframes, tribunals and where to get help, jurisdiction by jurisdiction.',
    },
    offline: {
      title: 'You are offline — Please.repair',
      description: 'Please.repair needs a connection to load this page. Reconnect and try again.',
    },
    serverError: {
      title: 'Something went wrong — Please.repair',
      description: 'This page could not be shown. Try again, or start from the home page.',
    },
    notFound: {
      title: 'Page not found — Please.repair',
      description: 'That page does not exist or has moved.',
    },
  },
  footer: {
    disclaimer:
      'Please.repair organises facts and points to official rules and forms. It does not give legal advice.',
    rights: '© 2026 Aperim Pty Ltd · ABN 46 150 699 737',
  },
};
